import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Check, AlertCircle, X, ExternalLink, RefreshCw, Sparkles, Loader2, Zap } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import RepublishBanner, { notifyPendingRepublishChanged } from "@/components/RepublishBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function SEO() {
    const [searchParams] = useSearchParams();
    const focusTarget = searchParams.get("focus"); // "title" | "meta" | null
    const titleRef = useRef(null);
    const metaRef = useRef(null);
    const [doctor, setDoctor] = useState(null);
    const [data, setData] = useState(null);
    const [health, setHealth] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ seo_title: "", meta_description: "", seo_keywords: [], seo_tagline: "" });
    const [kwInput, setKwInput] = useState("");

    async function loadAll() {
        try {
            const [dr, seo, hc] = await Promise.all([
                api.get("/me/doctor"),
                api.get("/me/seo"),
                api.get("/me/seo/health").catch(() => ({ data: null })),
            ]);
            setDoctor(dr.data);
            setData(seo.data);
            setHealth(hc.data);
            setForm({
                seo_title: seo.data?.seo_title || "",
                meta_description: seo.data?.meta_description || "",
                seo_keywords: seo.data?.seo_keywords || [],
                seo_tagline: seo.data?.seo_tagline || "",
            });
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => { loadAll(); }, []);

    // Deep-link support: `/doctor/seo?focus=title` and `?focus=meta` come
    // from the Publishing checklist. Scroll to and focus the requested
    // field once the editor has loaded.
    useEffect(() => {
        if (!data || !focusTarget) return;
        const ref = focusTarget === "meta" ? metaRef : (focusTarget === "title" ? titleRef : null);
        if (!ref?.current) return;
        // Small delay so layout is settled before scrolling.
        const t = setTimeout(() => {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            ref.current.focus?.();
        }, 150);
        return () => clearTimeout(t);
    }, [data, focusTarget]);

    async function save() {
        setSaving(true);
        try {
            await api.put("/me/seo", form);
            toast.success("SEO saved — republish to update your public page");
            await loadAll();
            notifyPendingRepublishChanged();
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving(false);
        }
    }

    function addKw() {
        const k = kwInput.trim();
        if (!k) return;
        if (form.seo_keywords.length >= 10) { toast.error("Max 10 keywords"); return; }
        if (form.seo_keywords.includes(k)) { setKwInput(""); return; }
        setForm({ ...form, seo_keywords: [...form.seo_keywords, k] });
        setKwInput("");
    }

    if (!data) {
        return <DoctorShell doctor={doctor}><div className="text-[#64748B]">Loading…</div></DoctorShell>;
    }

    if (data.needs_ai_first) {
        return (
            <DoctorShell doctor={doctor}>
                <div className="mb-6"><h1 className="ddi-section-title">SEO</h1></div>
                <Card className="border-[#F59E0B]/30 bg-[#FEF3C7]">
                    <CardContent className="py-6 flex items-center gap-3">
                        <AlertCircle className="text-[#B45309]" />
                        <div className="flex-1">
                            <div className="text-[13px] font-semibold text-[#92400E]">Complete onboarding first</div>
                            <div className="text-[12px] text-[#92400E]/80">You need an AI-refined profile before editing SEO. Submit your onboarding wizard to generate one.</div>
                        </div>
                        <Button onClick={() => window.location.href = "/doctor/onboarding"} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="seo-onboarding-cta">Open onboarding</Button>
                    </CardContent>
                </Card>
            </DoctorShell>
        );
    }

    const titleLen = (form.seo_title || "").length;
    const metaLen = (form.meta_description || "").length;

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="seo-header">
                <h1 className="ddi-section-title">SEO & Google Visibility</h1>
                <p className="ddi-section-sub">
                    Fine-tune the page title, meta description, keywords, and check your SEO health. Google indexing is never guaranteed — but these fundamentals give you the best chance.
                </p>
            </div>

            <RepublishBanner />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Editor */}
                <div className="lg:col-span-2 space-y-5">
                    <Card className="border-[#E5E9F2] ddi-shadow-sm">
                        <CardHeader className="pb-3"><CardTitle className="text-base">SEO fields</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <div className="flex items-baseline justify-between mb-1.5">
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B]">SEO title <span className="text-[#DC2626]">*</span></label>
                                    <span className={"text-[11px] " + (titleLen > 60 ? "text-[#DC2626]" : "text-[#64748B]")}>{titleLen}/60</span>
                                </div>
                                <Input ref={titleRef} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="Dr. Jane Doe — Cardiologist, Mumbai" data-testid="seo-title-input" maxLength={80} />
                            </div>
                            <div>
                                <div className="flex items-baseline justify-between mb-1.5">
                                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B]">Meta description <span className="text-[#DC2626]">*</span></label>
                                    <span className={"text-[11px] " + (metaLen > 160 ? "text-[#DC2626]" : "text-[#64748B]")}>{metaLen}/160</span>
                                </div>
                                <Textarea ref={metaRef} rows={3} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} placeholder="Short, factual summary shown in Google results." data-testid="seo-meta-input" maxLength={200} />
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Focus keywords (3–10)</label>
                                <div className="flex gap-2 mb-2">
                                    <Input value={kwInput} onChange={(e) => setKwInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); }}} placeholder="Add keyword and press Enter" data-testid="seo-kw-input" maxLength={40} />
                                    <Button onClick={addKw} className="bg-[#3A80F7] hover:bg-[#2D6BE0]">Add</Button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {(form.seo_keywords || []).map((k) => (
                                        <span key={k} className="ddi-chip ddi-chip-primary" data-testid={`seo-kw-${k}`}>
                                            {k}
                                            <button onClick={() => setForm({ ...form, seo_keywords: form.seo_keywords.filter((x) => x !== k) })} className="ml-1 hover:text-[#DC2626]"><X size={11} /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold uppercase tracking-widest text-[#64748B] mb-1.5 block">Tagline (public-page hero subhead)</label>
                                <Input value={form.seo_tagline} onChange={(e) => setForm({ ...form, seo_tagline: e.target.value })} placeholder="e.g. Preventive cardiology in Mumbai" data-testid="seo-tagline-input" maxLength={120} />
                            </div>
                            <Button onClick={save} disabled={saving || titleLen > 60 || metaLen > 160} className="bg-[#3A80F7] hover:bg-[#2D6BE0]" data-testid="seo-save-btn">
                                {saving ? "Saving…" : "Save changes"}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Google search preview */}
                    <Card className="border-[#E5E9F2] ddi-shadow-sm">
                        <CardHeader className="pb-3"><CardTitle className="text-base">Google preview</CardTitle></CardHeader>
                        <CardContent>
                            <div className="rounded-lg border border-[#E5E9F2] p-4 bg-white">
                                <div className="text-[13px] text-[#0F172A] mb-0.5 truncate">
                                    <span className="text-[#94A3B8] text-[11px]">{(health?.public_url || "your-domain.com/dr/your-slug").replace(/^https?:\/\//, "")}</span>
                                </div>
                                <div className="text-[18px] text-[#1a0dab] font-normal leading-snug hover:underline cursor-pointer" data-testid="seo-preview-title">
                                    {form.seo_title || "Your SEO title appears here"}
                                </div>
                                <div className="text-[13px] text-[#4d5156] leading-snug mt-1" data-testid="seo-preview-meta">
                                    {form.meta_description || "Your meta description appears here. Keep it under 160 characters, focused, and factual."}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* AI SEO Assistant (Phase 7) */}
                    <AiSeoAssistant onApplied={loadAll} formSetter={setForm} form={form} />
                </div>

                {/* Health checks + Google Search Console */}
                <div className="space-y-5">
                    <Card className="border-[#E5E9F2] ddi-shadow-sm">
                        <CardHeader className="pb-3 flex flex-row items-center justify-between">
                            <CardTitle className="text-base flex items-center gap-2"><Search size={18} className="text-[#3A80F7]" /> SEO Health</CardTitle>
                            <button onClick={loadAll} aria-label="Refresh SEO data" className="text-[#64748B] hover:text-[#3A80F7]" data-testid="seo-refresh"><RefreshCw size={14} /></button>
                        </CardHeader>
                        <CardContent>
                            {health ? (
                                <>
                                    <div className="mb-3">
                                        <div className="ddi-stat-value text-[#3A80F7]" data-testid="seo-score">{health.score}<span className="text-[#94A3B8] text-[16px]">/100</span></div>
                                        <div className="text-[11px] text-[#64748B]">
                                            <span className="text-[#16A34A]">{health.counts.pass} pass</span> · <span className="text-[#F59E0B]">{health.counts.warn} warn</span> · <span className="text-[#DC2626]">{health.counts.fail} fail</span>
                                        </div>
                                    </div>
                                    <ul className="divide-y divide-[#EEF1F6]">
                                        {health.checks.map((c) => (
                                            <li key={c.key} className="py-2 flex items-start gap-2 text-[13px]" data-testid={`seo-check-${c.key}`}>
                                                {c.status === "pass" ? <Check size={16} className="text-[#16A34A] shrink-0 mt-0.5" />
                                                    : c.status === "warn" ? <AlertCircle size={16} className="text-[#F59E0B] shrink-0 mt-0.5" />
                                                    : <X size={16} className="text-[#DC2626] shrink-0 mt-0.5" />}
                                                <div className="flex-1">
                                                    <div className="text-[#0F172A]">{c.label}</div>
                                                    {c.detail && <div className="text-[11px] text-[#64748B] mt-0.5">{c.detail}</div>}
                                                    {c.fix_link && c.status !== "pass" && <a href={c.fix_link} className="text-[11px] text-[#3A80F7] hover:underline">Fix →</a>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : <div className="text-[13px] text-[#64748B]">No health data.</div>}
                        </CardContent>
                    </Card>

                    <Card className="border-[#E5E9F2] ddi-shadow-sm">
                        <CardHeader className="pb-3"><CardTitle className="text-base">Google Search Console</CardTitle></CardHeader>
                        <CardContent>
                            <ol className="text-[12px] text-[#334155] space-y-2 list-decimal pl-4">
                                {(health?.google_search_console_instructions || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ol>
                            {health?.sitemap_url && (
                                <a href={health.sitemap_url} target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 text-[12px] text-[#3A80F7] hover:underline mt-3" data-testid="seo-sitemap-link">
                                    <ExternalLink size={12} /> View sitemap.xml
                                </a>
                            )}
                        </CardContent>
                    </Card>

                    <IndexingSubmissionsCard />
                    <GooglePerformanceCard />
                </div>
            </div>
        </DoctorShell>
    );
}


function GooglePerformanceCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        api.get("/indexing/me/performance").then((r) => { if (!cancelled) setData(r.data); })
           .catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);
    const a = data?.analytics || {};
    const ins = data?.inspection || {};
    const apiDisabled = (ins.error || "").toLowerCase().includes("api has not been used");
    return (
        <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="google-performance-card">
            <CardHeader className="pb-3"><CardTitle className="text-base">Google Performance (28-day)</CardTitle></CardHeader>
            <CardContent>
                {loading ? (
                    <div className="text-[12px] text-[#64748B]">Loading…</div>
                ) : !data?.published ? (
                    <div className="text-[12px] text-[#64748B]">{data?.notice || "Publish first."}</div>
                ) : apiDisabled ? (
                    <div className="text-[12px] text-[#D97706]">Search Console API is being enabled by the admin. Come back in a few minutes.</div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="rounded bg-[#F5F7FB] p-2 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Impressions</div>
                                <div className="text-lg font-bold text-[#123B5D]">{a.impressions ?? 0}</div>
                            </div>
                            <div className="rounded bg-[#F5F7FB] p-2 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Clicks</div>
                                <div className="text-lg font-bold text-[#123B5D]">{a.clicks ?? 0}</div>
                            </div>
                            <div className="rounded bg-[#F5F7FB] p-2 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Avg position</div>
                                <div className="text-lg font-bold text-[#123B5D]">{a.position ?? "—"}</div>
                            </div>
                            <div className="rounded bg-[#F5F7FB] p-2 text-center">
                                <div className="text-[10px] uppercase tracking-widest text-[#64748B]">Index</div>
                                <div className={"text-sm font-bold " + (ins.verdict === "PASS" ? "text-[#15803D]" : ins.verdict === "FAIL" ? "text-[#DC2626]" : "text-[#64748B]")}>
                                    {ins.verdict === "PASS" ? "✓" : ins.verdict === "FAIL" ? "✗" : "?"} {ins.coverage_state || ""}
                                </div>
                            </div>
                        </div>
                        {(a.top_queries || []).length > 0 && (
                            <>
                                <div className="text-[11px] font-semibold text-[#123B5D] mb-1">Top queries</div>
                                <ul className="text-[11px] space-y-1">
                                    {a.top_queries.map((q, i) => (
                                        <li key={i} className="flex justify-between text-[#334155]">
                                            <span className="truncate mr-2">{q.query}</span>
                                            <span className="text-[#64748B] tabular-nums">{q.impressions} impr · pos {q.position}</span>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {(data?.hints || []).length > 0 && (
                            <div className="mt-3 space-y-1.5">
                                {data.hints.map((h, i) => (
                                    <div key={i} className={"rounded p-2 text-[11px] " + (h.level === "high" ? "bg-[#FEE2E2] text-[#DC2626]" : h.level === "medium" ? "bg-[#FEF3C7] text-[#B45309]" : "bg-[#DBEAFE] text-[#1E40AF]")}>
                                        {h.message}
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="text-[10px] text-[#94A3B8] mt-3">{data.notice}</p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}


function IndexingSubmissionsCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get("/indexing/me");
                if (!cancelled) setData(r.data);
            } catch (e) { /* noop */ }
            finally { if (!cancelled) setLoading(false); }
        };
        load();
        return () => { cancelled = true; };
    }, []);
    return (
        <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="seo-indexing-card">
            <CardHeader className="pb-3">
                <CardTitle className="text-base">Auto-submitted to search engines</CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-[11px] text-[#64748B] mb-3">
                    On every publish, we notify Google (Indexing API) and Bing + Yandex (IndexNow) automatically. {data?.notice}
                </p>
                {loading ? (
                    <div className="text-[12px] text-[#64748B]">Loading…</div>
                ) : (data?.history || []).length === 0 ? (
                    <div className="text-[12px] text-[#64748B]">No submissions yet — publish/republish to trigger one.</div>
                ) : (
                    <div className="space-y-2" data-testid="seo-indexing-history">
                        {data.history.slice(0, 5).map((h) => (
                            <div key={h.id} className="rounded border border-[#E5E9F2] p-2 text-[11px]">
                                <div className="flex items-center justify-between">
                                    <span className={h.type === "URL_DELETED" ? "font-mono text-[#DC2626]" : "font-mono text-[#0F766E]"}>{h.type}</span>
                                    <span className="text-[#94A3B8]">{new Date(h.sent_at).toLocaleString()}</span>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                    <span className={"px-1.5 py-0.5 rounded " + (h.google?.ok ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#DC2626]")}>
                                        Google {h.google?.status_code || "—"}
                                    </span>
                                    <span className={"px-1.5 py-0.5 rounded " + (h.indexnow?.ok ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEE2E2] text-[#DC2626]")}>
                                        IndexNow {h.indexnow?.status_code || (h.indexnow?.skipped ? "skipped" : "—")}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}


function severityCls(s) {
    if (s === "high") return "ddi-chip ddi-chip-error";
    if (s === "medium") return "ddi-chip ddi-chip-warning";
    return "ddi-chip ddi-chip-neutral";
}


function AiSeoAssistant({ onApplied, form, formSetter }) {
    const [state, setState] = useState({ status: "idle", jobId: null, findings: null, error: null });
    const [applying, setApplying] = useState(null);

    async function analyze() {
        setState({ status: "queued", jobId: null, findings: null, error: null });
        try {
            const r = await api.post("/me/seo/ai-analysis");
            setState({ status: "processing", jobId: r.data.job_id, findings: null, error: null });
            poll(r.data.job_id);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed to start analysis";
            setState({ status: "error", jobId: null, findings: null, error: msg });
            toast.error(msg);
        }
    }

    async function poll(jobId) {
        let attempts = 0;
        const iv = setInterval(async () => {
            attempts += 1;
            try {
                const r = await api.get(`/me/seo/ai-analysis/${jobId}`);
                const s = r.data.job?.status;
                if (s === "completed") {
                    clearInterval(iv);
                    const findings = (r.data.output || {}).findings || [];
                    setState({ status: "completed", jobId, findings, error: null });
                } else if (s === "failed") {
                    clearInterval(iv);
                    setState({ status: "error", jobId, findings: null, error: r.data.job.error || "Failed" });
                }
            } catch {
                // ignore transient
            }
            if (attempts > 60) { clearInterval(iv); setState((p) => ({ ...p, status: "error", error: "Timed out" })); }
        }, 1500);
    }

    async function applySuggestion(f, field) {
        const value = (f.suggested_text || "").trim();
        if (!value) return;
        setApplying(f.key);
        try {
            await api.post(`/me/seo/ai-apply/${state.jobId}`, { field, value });
            toast.success("Applied — republish to make it live");
            if (field === "seo_title") formSetter({ ...form, seo_title: value });
            if (field === "meta_description") formSetter({ ...form, meta_description: value });
            notifyPendingRepublishChanged();
            if (onApplied) await onApplied();
        } catch (e) {
            toast.error(errMsg(e, "Failed to apply"));
        } finally { setApplying(null); }
    }

    function fieldGuessForFinding(f) {
        const k = (f.key || "").toLowerCase();
        if (k.includes("title")) return "seo_title";
        if (k.includes("meta") || k.includes("description")) return "meta_description";
        return null;
    }

    return (
        <Card className="border-[#E5E9F2] ddi-shadow-sm" data-testid="ai-seo-assistant">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles size={18} className="text-[#3A80F7]" /> AI SEO Assistant
                </CardTitle>
                <Button
                    onClick={analyze}
                    disabled={state.status === "queued" || state.status === "processing"}
                    className="bg-[#3A80F7] hover:bg-[#2D6BE0]"
                    data-testid="ai-seo-analyze-btn"
                >
                    {state.status === "queued" || state.status === "processing" ? (
                        <><Loader2 size={13} className="animate-spin mr-2" /> Analyzing…</>
                    ) : (
                        <><Zap size={13} className="mr-2" /> Run AI analysis</>
                    )}
                </Button>
            </CardHeader>
            <CardContent>
                {state.status === "idle" && (
                    <div className="text-[13px] text-[#64748B]">
                        Get grounded, one-click applicable SEO suggestions from AI based on your current profile data. Uses your daily AI budget (20/day).
                    </div>
                )}
                {(state.status === "queued" || state.status === "processing") && (
                    <div className="py-6 text-center text-[13px] text-[#64748B]">
                        <Loader2 size={20} className="animate-spin mx-auto mb-2 text-[#3A80F7]" />
                        Waiting for AI response…
                    </div>
                )}
                {state.status === "error" && (
                    <div className="py-4 text-[13px] text-[#DC2626]">
                        <AlertCircle size={14} className="inline mr-1" /> {state.error}
                    </div>
                )}
                {state.status === "completed" && (
                    <>
                        {state.findings.length === 0 ? (
                            <div className="text-[13px] text-[#16A34A] py-4 flex items-center gap-2">
                                <Check size={16} /> AI found no critical SEO issues. Well done!
                            </div>
                        ) : (
                            <ul className="space-y-3" data-testid="ai-seo-findings">
                                {state.findings.map((f, idx) => {
                                    const field = fieldGuessForFinding(f);
                                    const canApply = field && f.suggested_text;
                                    return (
                                        <li key={f.key || idx} className="rounded-lg border border-[#E5E9F2] p-3" data-testid={`ai-seo-finding-${idx}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={severityCls(f.severity)}>{f.severity}</span>
                                                <span className="text-[13px] font-semibold text-[#0F172A]">{f.issue}</span>
                                            </div>
                                            <div className="text-[12px] text-[#64748B] mb-2">{f.recommendation}</div>
                                            {f.suggested_text && (
                                                <div className="text-[12px] bg-[#F5F7FB] rounded p-2 border border-[#E5E9F2]">
                                                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold mb-1">Suggested</div>
                                                    <div className="text-[#0F172A]">{f.suggested_text}</div>
                                                </div>
                                            )}
                                            {canApply && (
                                                <div className="mt-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => applySuggestion(f, field)}
                                                        disabled={applying === f.key}
                                                        className="bg-[#3A80F7] hover:bg-[#2D6BE0] text-[12px] h-7"
                                                        data-testid={`ai-seo-apply-${idx}`}
                                                    >
                                                        {applying === f.key ? "Applying…" : `Apply to ${field.replace("_", " ")}`}
                                                    </Button>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

